import axios from "axios";
import { formatDateToCustomFormat } from '../utils/dateUtils';
import { formatAmountForDisplay } from '../utils/amountUtils';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Important for cookies
});

// Agregar un interceptor de solicitud para añadir el token a todas las peticiones
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add a response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Solo redirigir si el error es 401 y no estamos en la página de login o register
    // y no es un error de cambio de contraseña
    if (
      error.response?.status === 401 &&
      !window.location.pathname.includes("/login") &&
      !window.location.pathname.includes("/register") &&
      !error.response?.data?.error?.includes("invalid_current_password")
    ) {
      console.error("Sesión expirada o no autorizada");
      // Limpiar el token
      localStorage.removeItem("token");
      // Redirigir al login
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

export const registerUser = async (userData) => {
  try {
    const response = await api.post("/register", userData);
    return response.data;
  } catch (error) {
    if (error.response) {
      // Return status code for client-side error handling
      throw new Error(error.response.data.message || `Request failed with status code ${error.response.status}`);
    }
    throw error;
  }
};

export const loginUser = async (credentials) => {
  try {
    const response = await api.post("/login", credentials);
    // Store the JWT token in localStorage
    if (response.data.token) {
      localStorage.setItem("token", response.data.token);
      // Set the token in axios default headers
      api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
    }
    return response.data;
  } catch (error) {
    // Para el login, preservamos el error original para que el componente
    // pueda acceder a error.response.data.error para manejar certificate_required
    throw error;
  }
};

export const logoutUser = async () => {
  try {
    // Make the logout request to the backend
    await api.post("/logout");
    // Clear localStorage
    localStorage.removeItem("token");
    // Force a page reload to ensure all state is cleared
    window.location.href = "/login";
  } catch (error) {
    console.error("Logout error:", error);
    // Even if there's an error, clear localStorage and redirect to login
    localStorage.removeItem("token");
    window.location.href = "/login";
  }
};

export const getProfile = async () => {
  try {
    const response = await api.get("/profile");
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data.message || `Request failed with status code ${error.response.status}`);
    }
    throw error;
  }
};

export const updateProfile = async (profileData) => {
  try {
    const response = await api.put("/profile", profileData);
    return response.data;
  } catch (error) {
    if (error.response) {
      // Handle specific error codes
      if (error.response.status === 400) {
        if (error.response.data.error === 'invalid_email') {
          throw new Error('Formato de email inválido');
        }
        if (error.response.data.error === 'invalid_lawyer_data') {
          throw new Error('Datos de abogado inválidos');
        }
        if (error.response.data.error === 'missing_lawyer_fields') {
          throw new Error('Faltan campos requeridos para abogado');
        }
        // Si hay un mensaje específico del backend, usarlo
        if (error.response.data.message) {
          throw new Error(error.response.data.message);
        }
        throw new Error('Datos del perfil inválidos');
      }
      if (error.response.status === 409) {
        throw new Error('El email ya está registrado por otro usuario');
      }
      if (error.response.status === 422) {
        throw new Error(error.response.data.message || 'Datos de validación incorrectos');
      }
      throw new Error(error.response.data.message || 'Error al actualizar el perfil');
    }
    throw error;
  }
};

export const changePassword = async (passwordData) => {
  try {
    const response = await api.put("/password", passwordData);
    return response.data;
  } catch (error) {
    if (error.response) {
      // Handle specific error codes
      if (error.response.status === 400) {
        if (error.response.data.error === 'missing_fields') {
          throw new Error('Faltan campos requeridos');
        }
        if (error.response.data.error === 'invalid_password') {
          throw new Error('La nueva contraseña debe tener entre 8 y 16 caracteres alfanuméricos');
        }
      }
      if (error.response.status === 401 && error.response.data.error === 'invalid_current_password') {
        throw new Error('La contraseña actual no es correcta');
      }
      throw new Error(error.response.data.message || 'Error al cambiar la contraseña');
    }
    throw error;
  }
};

export const getProvinces = async () => {
  try {
    const response = await api.get("/provinces");
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Offer API services

// Create a new offer
export const createOffer = async (offerData, includesFiles = false) => {
  try {
    let config = {};
    let formattedData = offerData;
    
    // Ensure the encryption flag is set - forzar siempre encriptación
    if (!formattedData.encrypt_ids) {
      console.log("Añadiendo flag encrypt_ids=true para garantizar encriptación");
      formattedData.encrypt_ids = true;
    }
    
    if (includesFiles) {
      // If the request includes files, use FormData and set the appropriate content type
      const formData = new FormData();
      
      // Helper function to append nested objects with bracket notation
      const appendNestedObject = (obj, prefix) => {
        Object.keys(obj).forEach(key => {
          if (obj[key] !== null && obj[key] !== undefined) {
            formData.append(`${prefix}[${key}]`, obj[key]);
          }
        });
      };
      
      // Add all non-file and non-nested-object fields
      Object.keys(offerData).forEach(key => {
        if (key !== 'files' && key !== 'offeror' && key !== 'counterparty' && key !== 'lawyer') {
          formData.append(key, offerData[key]);
        }
      });
      
      // Add nested objects with bracket notation
      if (offerData.offeror) {
        appendNestedObject(offerData.offeror, 'offeror');
      }
      if (offerData.counterparty) {
        appendNestedObject(offerData.counterparty, 'counterparty');
      }
      if (offerData.lawyer) {
        appendNestedObject(offerData.lawyer, 'lawyer');
      }
      
      // Siempre asegurar que encrypt_ids esté presente y sea true
      formData.delete('encrypt_ids'); // Eliminar si ya existe
      formData.append('encrypt_ids', 'true');
      console.log("Establecido encrypt_ids=true en FormData");
      
      // Add replaceExistingFiles parameter (default to true for updates)
      formData.append('replaceExistingFiles', 'true');
      
      // Add files if they exist
      if (offerData.files && offerData.files.length > 0) {
        offerData.files.forEach(file => {
          formData.append('files', file);
        });
      }
      
      formattedData = formData;
      config = {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      };
    }
    
    // Log para verificar que la flag está presente
    if (formattedData instanceof FormData) {
      console.log("Enviando FormData con encrypt_ids:", formattedData.get('encrypt_ids'));
      console.log("Enviando FormData con replaceExistingFiles:", formattedData.get('replaceExistingFiles'));
    } else {
      console.log("Enviando JSON con encrypt_ids:", formattedData.encrypt_ids);
    }
    
    const response = await api.post("/offers", formattedData, config);
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Error creating offer');
    }
    throw error;
  }
};

// Get all offers for the user
export const getUserOffers = async (params = {}) => {
  try {
    console.log("🌐 getUserOffers called with params:", params);
    
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.search) queryParams.append('search', params.search);
    if (params.searchFields) queryParams.append('searchFields', params.searchFields);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if(params.getAllOfStatus) queryParams.append('getAllOfStatus', params.getAllOfStatus);
    
    // Nuevos parámetros para la API optimizada
    if (params.includeCounts !== undefined) queryParams.append('includeCounts', params.includeCounts);
    if (params.includeExpiring !== undefined) queryParams.append('includeExpiring', params.includeExpiring);

    const url = `/offers?${queryParams.toString()}`;
    console.log("🌐 Final URL being called:", url);
    console.log("🌐 Search parameter specifically:", params.search);
    
    const response = await api.get(url);
    
    console.log("🌐 Response received from API:", response.data);
    
    // La nueva API devuelve:
    // {
    //   offers: [...],
    //   totalItems: number,
    //   totalPages: number,
    //   currentPage: number,
    //   hasNextPage: boolean,
    //   hasPrevPage: boolean,
    //   statusCounts: { all, draft, sent, accepted, rejected, counteroffered, finalized, expired, expiring_in_5_days },
    //   statusBreakdown: { ... },
    //   pagination: { ... },
    //   appliedFilters: { ... },
    //   meta: { ... }
    // }
    
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Error fetching offers');
    }
    throw error;
  }
};

// Get a specific offer by ID
export const getOfferById = async (offerId) => {
  try {
    const response = await api.get(`/offers/${offerId}`);
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Error fetching offer');
    }
    throw error;
  }
};

// Update an existing offer
export const updateOffer = async (offerId, offerData, includesFiles = false) => {
  try {
    let config = {};
    let formattedData = offerData;
    
    // Ensure the encryption flag is set - forzar siempre encriptación
    if (!formattedData.encrypt_ids) {
      console.log("Añadiendo flag encrypt_ids=true para garantizar encriptación en actualización");
      formattedData.encrypt_ids = true;
    }
    
    if (includesFiles) {
      // If the request includes files, use FormData and set the appropriate content type
      const formData = new FormData();
      
      // Helper function to append nested objects with bracket notation
      const appendNestedObject = (obj, prefix) => {
        Object.keys(obj).forEach(key => {
          if (obj[key] !== null && obj[key] !== undefined) {
            formData.append(`${prefix}[${key}]`, obj[key]);
          }
        });
      };
      
      // Add all non-file and non-nested-object fields
      Object.keys(offerData).forEach(key => {
        if (key !== 'files' && key !== 'offeror' && key !== 'counterparty' && key !== 'lawyer') {
          formData.append(key, offerData[key]);
        }
      });
      
      // Add nested objects with bracket notation
      if (offerData.offeror) {
        appendNestedObject(offerData.offeror, 'offeror');
      }
      if (offerData.counterparty) {
        appendNestedObject(offerData.counterparty, 'counterparty');
      }
      if (offerData.lawyer) {
        appendNestedObject(offerData.lawyer, 'lawyer');
      }
      
      // Siempre asegurar que encrypt_ids esté presente y sea true
      formData.delete('encrypt_ids'); // Eliminar si ya existe
      formData.append('encrypt_ids', 'true');
      console.log("Establecido encrypt_ids=true en FormData para actualización");
      
      // Add replaceExistingFiles parameter (default to true for updates)
      formData.append('replaceExistingFiles', 'true');
      
      // Add files if they exist
      if (offerData.files && offerData.files.length > 0) {
        offerData.files.forEach(file => {
          formData.append('files', file);
        });
      }
      
      formattedData = formData;
      config = {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      };
    }
    
    // Log para verificar que la flag está presente
    if (formattedData instanceof FormData) {
      console.log("Enviando FormData en actualización con encrypt_ids:", formattedData.get('encrypt_ids'));
      console.log("Enviando FormData en actualización con replaceExistingFiles:", formattedData.get('replaceExistingFiles'));
    } else {
      console.log("Enviando JSON en actualización con encrypt_ids:", formattedData.encrypt_ids);
    }
    
    const response = await api.put(`/offers/${offerId}`, formattedData, config);
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Error updating offer');
    }
    throw error;
  }
};

// Delete an offer by ID
export const deleteOffer = async (offerId) => {
  try {
    const response = await api.delete(`/offers/${offerId}`);
    return response.data; // Or handle success/failure based on API response
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Error deleting offer');
    }
    throw error;
  }
};

// Mark offer as read
export const markOfferAsRead = async (offerId) => {
  try {
    const response = await api.put(`/offers/${offerId}/mark-read`);
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Error marking offer as read');
    }
    throw error;
  }
};

// Delete a specific file from an offer
export const deleteOfferFile = async (offerId, fileId) => {
  try {
    const response = await api.delete(`/offers/${offerId}/files/${fileId}`);
    return response.data;
  } catch (error) {
    if (error.response) {
      // Handle specific error cases
      switch (error.response.status) {
        case 400:
          throw new Error("Parámetros inválidos - ID de oferta o archivo no válidos");
        case 401:
          throw new Error("Usuario no autenticado");
        case 403:
          throw new Error("No tienes permisos para eliminar este archivo o la oferta no está en estado borrador");
        case 404:
          throw new Error("Oferta o archivo no encontrado");
        case 500:
          throw new Error("Error del servidor al eliminar el archivo");
        default:
          throw new Error(error.response.data.message || 'Error eliminando archivo');
      }
    }
    throw error;
  }
};

// Download a specific file from an offer (compatible with token and user session)
export const downloadOfferFile = async (offerId, fileId, token = null, expectedFileName = null) => {
  try {
    let config = {
      responseType: 'blob' // Important for downloading files
    };
    
    let url = `/offers/${offerId}/files/${fileId}`;
    
    if (token) {
      // If token is provided, add it as query parameter or header
      url += `?token=${token}`;
    }
    // If no token, the Authorization header will be added by the interceptor
    
    const response = await api.get(url, config);
    
    // Extract Content-Type from response headers
    const contentType = response.headers['content-type'] || response.headers['Content-Type'] || 'application/octet-stream';
    console.log("Content-Type del archivo:", contentType);
    
    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
    // Use expectedFileName as default, fallback to generic name if not provided
    let filename = expectedFileName || `archivo-${fileId}`;
    
    if (contentDisposition) {
      console.log("Content-Disposition header:", contentDisposition);
      // Try different patterns for filename extraction
      const patterns = [
        /filename\*=UTF-8''([^;]+)/,  // RFC 5987 format
        /filename\*=([^;]+)/,         // RFC 5987 without UTF-8
        /filename="([^"]+)"/,         // Quoted filename
        /filename=([^;]+)/            // Unquoted filename
      ];
      
      for (const pattern of patterns) {
        const matches = contentDisposition.match(pattern);
        if (matches && matches[1]) {
          filename = decodeURIComponent(matches[1].trim());
          break;
        }
      }
    }
    
    console.log("Nombre de archivo extraído:", filename);
    
    // Create blob with correct content type
    const blob = new Blob([response.data], { type: contentType });
    
    // Create download link
    const url_obj = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url_obj;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url_obj);
    
    return true;
  } catch (error) {
    if (error.response) {
      // Handle specific error cases
      switch (error.response.status) {
        case 400:
          if (error.response.data.error === 'token_mismatch') {
            throw new Error("El token no corresponde a esta oferta");
          }
          throw new Error("Parámetros inválidos");
        case 401:
          throw new Error("Se requiere autenticación para descargar archivos");
        case 403:
          if (error.response.data.error === 'invalid_offer_status') {
            throw new Error("La oferta no está en un estado que permita descargar archivos");
          }
          throw new Error("No tienes permisos para acceder a los archivos de esta oferta");
        case 404:
          if (error.response.data.error === 'File not found') {
            throw new Error("Archivo no encontrado en esta oferta");
          }
          throw new Error("Oferta no encontrada");
        case 500:
          throw new Error("Error del servidor al acceder al archivo");
        default:
          throw new Error(error.response.data.message || 'Error descargando archivo');
      }
    }
    throw new Error("No se pudo conectar con el servidor. Por favor, verifica tu conexión a internet.");
  }
};

// Send an offer to the counterparty through Evidence
export const sendOffer = async (offerId) => {
  try {
    const response = await api.post(`/offers/${offerId}/send`);
    return response.data;
  } catch (error) {
    if (error.response) {
      // Pass the full error response so we can check status codes
      throw error;
    }
    throw error;
  }
};

// Offer Response Endpoints

// Validate token and get offer
export const validateOfferToken = async (token) => {
  try {
    const response = await api.get(`/offers/validate-token?token=${token}`);
    
    // Manejar los diferentes escenarios de respuesta según la documentación de la API
    if (response.data.redirectTo === "/login") {
      console.log("Se requiere inicio de sesión para esta oferta");
    }
    
    if (response.data.clearSession) {
      console.log("Usuario actual no autorizado para esta oferta, limpiando sesión");
      localStorage.removeItem("token");
    }
    
    return response.data;
  } catch (error) {
    if (error.response) {
      // Capturar detalles específicos del error
      if (error.response.status === 400 && error.response.data) {
        // Si tenemos información detallada sobre el error, la incluimos en el mensaje
        const errorData = error.response.data;
        throw new Error(
          errorData.error 
            ? `${errorData.error}: ${errorData.message || 'Error validating token'}` 
            : errorData.message || 'Error validating token'
        );
      }
      throw new Error(error.response.data.message || 'Error validating token');
    }
    throw error;
  }
};

// Accept offer
export const acceptOffer = async (offerId, token, acceptanceData) => {
  try {
    const response = await api.post(`/offers/${offerId}/accept`, {
      token,
      comments: acceptanceData.address,
      counterpartyDni: acceptanceData.counterpartyDni,
      lawyerDni: acceptanceData.lawyerDni
    });
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Error accepting offer');
    }
    throw error;
  }
};

// Reject offer
export const rejectOffer = async (offerId, token, reason = "") => {
  try {
    const response = await api.post(`/offers/${offerId}/reject`, {
      token,
      reason
    });
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Error rejecting offer');
    }
    throw error;
  }
};

// Counteroffer
export const counterOffer = async (offerId, counterOfferText, confidentiality = null) => {
  try {
    const payload = {
      counterOfferText
    };
    
    // Solo incluir confidentiality si se proporciona explícitamente
    if (confidentiality !== null) {
      payload.confidentiality = confidentiality;
    }
    
    const response = await api.post(`/offers/${offerId}/counteroffer`, payload);
    return response.data;
  } catch (error) {
    if (error.response) {
      // Handle specific error cases
      switch (error.response.status) {
        case 400:
          throw new Error("Falta el texto de la contraoferta o formato inválido");
        case 401:
          throw new Error("Sesión inválida o usuario no autenticado");
        case 403:
          throw new Error("Estado de oferta inválido o usuario no autorizado");
        case 404:
          throw new Error("Oferta no encontrada");
        case 500:
          throw new Error("Error del servidor");
        default:
          throw new Error(error.response.data.message || 'Error creating counteroffer');
      }
    }
    throw error;
  }
};

// Reformulate offer - NEW endpoint according to API documentation
export const reformulateOffer = async (offerId, reformulationData, includesFiles = false) => {
  try {
    let config = {};
    let formattedData = reformulationData;
    
    if (includesFiles) {
      // If the request includes files, use FormData and set the appropriate content type
      const formData = new FormData();
      
      // Add files if they exist
      if (reformulationData.files && reformulationData.files.length > 0) {
        reformulationData.files.forEach((file) => {
          formData.append('files', file);
        });
      }
      
      // Add newDeadline
      if (reformulationData.newDeadline) {
        formData.append('newDeadline', reformulationData.newDeadline);
      }
      
      formattedData = formData;
      config.headers = {
        'Content-Type': 'multipart/form-data',
      };
    } else {
      // Text-only reformulation
      formattedData = {
        reformulationText: reformulationData.reformulationText,
        newDeadline: reformulationData.newDeadline
      };
    }
    
    const response = await api.post(`/offers/${offerId}/reformulate`, formattedData, config);
    return response.data;
  } catch (error) {
    if (error.response) {
      switch (error.response.status) {
        case 400:
          throw new Error("Datos inválidos, nueva fecha límite incorrecta o contenido mixto no permitido");
        case 401:
          throw new Error("Usuario no autenticado");
        case 403:
          throw new Error("Solo el oferente puede reformular o estado inválido");
        case 404:
          throw new Error("Oferta no encontrada");
        case 500:
          throw new Error("Error del servidor o al guardar archivos");
        default:
          throw new Error(error.response.data.message || 'Error creating reformulation');
      }
    }
    throw error;
  }
};

// Download reformulation file
export const downloadReformulationFile = async (offerId, fileId, expectedFileName = null) => {
  try {
    console.log(`🔽 Iniciando descarga de archivo de reformulación - OfferId: ${offerId}, FileId: ${fileId}`);
    console.log(`📁 Nombre de archivo esperado: ${expectedFileName}`);
    
    const response = await api.get(`/offers/${offerId}/reformulation/files/${fileId}`, {
      responseType: 'blob'
    });
    
    console.log("📋 Headers de respuesta completos:", response.headers);
    
    // Extract Content-Type from response headers
    const contentType = response.headers['content-type'] || response.headers['Content-Type'] || 'application/octet-stream';
    console.log("📄 Content-Type del archivo de reformulación:", contentType);
    
    // Use expectedFileName as priority, fallback to Content-Disposition header
    let filename = expectedFileName || `reformulation_file_${fileId}`;
    
    if (!expectedFileName) {
      // Only try to extract from Content-Disposition if no expectedFileName provided
      const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
      
      if (contentDisposition) {
        console.log("📎 Content-Disposition header:", contentDisposition);
        // Try different patterns for filename extraction
        const patterns = [
          /filename\*=UTF-8''([^;]+)/,  // RFC 5987 format
          /filename\*=([^;]+)/,         // RFC 5987 without UTF-8
          /filename="([^"]+)"/,         // Quoted filename
          /filename=([^;]+)/            // Unquoted filename
        ];
        
        for (const pattern of patterns) {
          const matches = contentDisposition.match(pattern);
          if (matches && matches[1]) {
            filename = decodeURIComponent(matches[1].trim());
            console.log(`✅ Nombre de archivo extraído con patrón ${pattern}:`, filename);
            break;
          }
        }
      } else {
        console.log("⚠️ No se encontró header Content-Disposition");
      }
    } else {
      console.log("✅ Usando nombre de archivo proporcionado:", filename);
    }
    
    console.log("📁 Nombre de archivo final:", filename);
    
    // Create blob with correct content type
    const blob = new Blob([response.data], { type: contentType });
    console.log("💾 Blob creado con tipo:", blob.type, "y tamaño:", blob.size);
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    console.log("✅ Descarga completada exitosamente");
    return { success: true, filename };
  } catch (error) {
    console.error("❌ Error en downloadReformulationFile:", error);
    if (error.response) {
      console.error("📄 Status de error:", error.response.status);
      console.error("📄 Data de error:", error.response.data);
      switch (error.response.status) {
        case 400:
          throw new Error("Parámetros inválidos");
        case 401:
          throw new Error("Usuario no autenticado");
        case 403:
          throw new Error("Sin permisos para acceder a este archivo");
        case 404:
          throw new Error("Oferta o archivo no encontrado");
        case 500:
          throw new Error("Error de acceso al archivo");
        default:
          throw new Error(error.response.data.message || 'Error downloading reformulation file');
      }
    }
    throw error;
  }
};

// Delete reformulation file
export const deleteReformulationFile = async (offerId, fileId) => {
  try {
    const response = await api.delete(`/offers/${offerId}/reformulation/files/${fileId}`);
    return response.data;
  } catch (error) {
    if (error.response) {
      switch (error.response.status) {
        case 400:
          throw new Error("Parámetros inválidos");
        case 401:
          throw new Error("Usuario no autenticado");
        case 403:
          throw new Error("Sin permisos para eliminar este archivo o estado inválido");
        case 404:
          throw new Error("Oferta o archivo no encontrado");
        case 500:
          throw new Error("Error del servidor al eliminar archivo");
        default:
          throw new Error(error.response.data.message || 'Error deleting reformulation file');
      }
    }
    throw error;
  }
};

// Respond to reformulation - NEW endpoint
export const respondToReformulation = async (offerId, response, acceptanceData) => {
  try {
    const apiResponse = await api.post(`/offers/${offerId}/respond-reformulation`, {
      response,
      comments: acceptanceData.address || "",
      counterpartyDni: acceptanceData.counterpartyDni || "",
      lawyerDni: acceptanceData.lawyerDni || ""
    });
    return apiResponse.data;
  } catch (error) {
    if (error.response) {
      switch (error.response.status) {
        case 400:
          throw new Error('Respuesta inválida (debe ser "accept" o "reject")');
        case 401:
          throw new Error("Usuario no autenticado");
        case 403:
          throw new Error("Usuario no autorizado o estado de oferta inválido");
        case 404:
          throw new Error("Oferta no encontrada");
        case 500:
          throw new Error("Error del servidor");
        default:
          throw new Error(error.response.data.message || 'Error responding to reformulation');
      }
    }
    throw error;
  }
};

// Get offers with tab filtering - NEW endpoint
export const getOffersWithTabs = async (tab = 'todos', page = 1, limit = 50) => {
  try {
    const response = await api.get('/offers/my-offers', {
      params: { tab, page, limit }
    });
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Error fetching offers with tabs');
    }
    throw error;
  }
};

// DEPRECATED - Remove this function, replace with reformulateOffer
export const submitReformulation = async (offerId, reformulationData, includesFiles = false) => {
  console.warn("submitReformulation is deprecated. Use reformulateOffer instead.");
  throw new Error("Esta función está obsoleta. Use reformulateOffer en su lugar.");
};

// Download offer PDF - works with authenticated users or with token
export const downloadOfferPdf = async (offerId, token = null) => {
  let originalOverflow; // Declarar fuera del try para usar en catch
  let container; // Declarar fuera del try para usar en catch
  
  try {
    let offerData;
    let currentUser = null;
    
    if (token) {
      // Si hay token, usar validate-token endpoint
      const response = await api.get(`/offers/validate-token?token=${token}`);
      offerData = response.data.offer;
      console.log("---------------- DEBUG - Offer data from validate-token:", offerData);
      try {
        currentUser = await getProfile();
        console.log("🔍 DEBUG - Raw getProfile() response:", currentUser);
        
        // Handle the case where getProfile returns { user: { ... } } format
        if (currentUser.user) {
          currentUser = currentUser.user;
          console.log("🔍 DEBUG - Extracted user data from nested structure:", currentUser);
        }
      } catch (userError) {
        console.warn("No se pudieron obtener los datos del usuario:", userError);
      }
    } else {
      // Si no hay token, usar getOfferById (para usuarios autenticados)
      offerData = await getOfferById(offerId);
      console.log("---------------- DEBUG - Offer data from getOfferById:", offerData);
      // Obtener datos del usuario actual para el representante
      try {
        currentUser = await getProfile();
        console.log("🔍 DEBUG - Raw getProfile() response:", currentUser);
        
        // Handle the case where getProfile returns { user: { ... } } format
        if (currentUser.user) {
          currentUser = currentUser.user;
          console.log("🔍 DEBUG - Extracted user data from nested structure:", currentUser);
        }
      } catch (userError) {
        console.warn("No se pudieron obtener los datos del usuario:", userError);
      }
    }
    
    // Importar html2pdf dinámicamente solo cuando se necesite
    const html2pdf = await import('html2pdf.js');
    
    // Crear un elemento div temporal para renderizar el contenido
    container = document.createElement('div');
    container.style.padding = '20px';
    container.style.fontFamily = 'Lato, Arial, sans-serif';
    
    // Función para formatear la fecha
    const formatDate = (dateString) => {
      if (!dateString) return "";
      return formatDateToCustomFormat(dateString);
    };
    
    // Función para formatear fecha solo con día/mes/año (sin hora)
    const formatDateOnly = (dateString) => {
      if (!dateString) return "";
      const formatted = formatDateToCustomFormat(dateString);
      // Remover la parte de la hora si está presente (formato: "DD/MM/YYYY, HH:MM:SS" -> "DD/MM/YYYY")
      return formatted.split(',')[0].trim();
    };
    
    // Usar la función unificada de amountUtils
    const formatAmount = formatAmountForDisplay;
    
    // Mapear los datos a la estructura necesaria
    const mapApiData = (offerData, currentUser) => {
      // Extraer datos de todas las posibles fuentes en la API
      // Priorizar datos del usuario actual si están disponibles
      const userData = currentUser || offerData.user || {}; // Representante de la parte ofertante
      const lawyerData = offerData.lawyer || {};
      const offerorData = offerData.offeror || {};
      const counterpartyData = offerData.counterparty || {};
      
      // Log para depurar los datos del usuario
      console.log("🔍 DEBUG - Current user data:", currentUser);
      console.log("🔍 DEBUG - Offer user data:", offerData.user);
      console.log("🔍 DEBUG - Final userData for representante:", userData);
      
      // Log específico para campos del representante
      console.log("🔍 DEBUG - Representante fields:");
      console.log("  - nombre:", userData.firstName || userData.first_name || userData.name || '');
      console.log("  - apellidos:", userData.lastNames || userData.last_name || userData.lastName || '');
      console.log("  - identificacion:", userData.idValue || userData.id_number || userData.identityNumber || userData.identificationNumber || '');
      console.log("  - email:", userData.email || '');
      console.log("  - movil:", userData.mobile || userData.mobile_phone || userData.phone || '');
      console.log("  - numColegiado:", userData.membershipNumber || userData.lawyer_membership_number || userData.lawyerMembershipNumber || '');
      console.log("  - provinciaColegio:", userData.province || userData.province_name || userData.provinceName || '');
      
      return {
        representante: {
          nombre: userData.firstName || userData.first_name || userData.name || '',
          apellidos: userData.lastNames || userData.last_name || userData.lastName || '',
          identificacion: userData.idValue || userData.id_number || userData.identityNumber || userData.identificationNumber || '',
          email: userData.email || '',
          movil: userData.mobile || userData.mobile_phone || userData.phone || '',
          numColegiado: userData.membershipNumber || userData.lawyer_membership_number || userData.lawyerMembershipNumber || '',
          provinciaColegio: userData.province || userData.province_name || userData.provinceName || ''
        },
        ofertante: {
          nombre: offerorData.firstName || offerData.representedparty_name || '',
          apellidos: offerorData.lastNames || offerData.representedparty_last_name || '',
          identificacion: offerorData.idValue || offerData.representedparty_id || '',
          email: offerorData.email || offerData.representedparty_email || '',
          movil: offerorData.mobile || offerData.representedparty_phone || ''
        },
        contraparte: {
          nombre: counterpartyData.firstName || offerData.counterparty_name || '',
          apellidos: counterpartyData.lastNames || offerData.counterparty_last_name || '',
          identificacion: counterpartyData.idValue || offerData.counterparty_id || '',
          email: counterpartyData.email || offerData.counterparty_email || '',
          movil: counterpartyData.mobile || offerData.counterparty_phone || ''
        },
        representacionContraparte: {
          activa: !!(lawyerData.firstName || lawyerData.lastNames || lawyerData.email || offerData.lawyer_name || offerData.lawyer_email),
          nombre: lawyerData.firstName || offerData.lawyer_name || '',
          apellidos: lawyerData.lastNames || offerData.lawyer_last_name || '',
          identificacion: lawyerData.idValue || offerData.lawyer_id || '',
          email: lawyerData.email || offerData.lawyer_email || '',
          movil: lawyerData.mobile || offerData.lawyer_phone || ''
        },
        referencia: offerData.reference || offerData.offer_reference || '',
        objeto: offerData.subject || offerData.offer_subject || '',
        monto: formatAmount(offerData.amount || offerData.offer_total_amount),
        confidencialidad: offerData.confidential || offerData.offer_confidentiality || false,
        plazoRespuesta: formatDateOnly(offerData.deadlineToRespond || offerData.offer_response_deadline),
        contenido: offerData.writtenContent || offerData.offer_user_content || '',
        creationDate: offerData.creationDate || offerData.offer_creation_date || new Date().toISOString(),
        archivos: offerData.files || [], // Archivos adjuntos
        
        // Datos de contraoferta
        contraoferta: {
          texto: offerData.counteroffer_text || offerData.counterofferText || '',
          archivos: offerData.counterOfferFiles || offerData.counterofferFiles || []
        },
        
        // Datos de reformulación
        reformulacion: {
          texto: offerData.reformulation_text || offerData.reformulationText || offerData.reformulation_content || '',
          archivos: offerData.reformulationFiles || [],
          nuevaFechaLimite: formatDate(offerData.reformulation_deadline || offerData.reformulationDeadline || '')
        },
        
        // Estado de la oferta para determinar qué secciones mostrar
        estado: offerData.status || ''
      };
    };
    
    // Transformar los datos
    const mappedData = mapApiData(offerData, currentUser);
    
    // Log para depurar los datos de contraoferta y reformulación
    console.log("🔍 DEBUG - Datos de contraoferta en PDF:", {
      estado: mappedData.estado,
      contraofertaTexto: mappedData.contraoferta.texto,
      contraofertaArchivos: mappedData.contraoferta.archivos,
      reformulacionTexto: mappedData.reformulacion.texto,
      reformulacionArchivos: mappedData.reformulacion.archivos,
      reformulacionFechaLimite: mappedData.reformulacion.nuevaFechaLimite
    });
    
    console.log("🔍 DEBUG - Datos originales de la API:", {
      counteroffer_text: offerData.counteroffer_text,
      counterofferText: offerData.counterofferText,
      counterOfferFiles: offerData.counterOfferFiles,
      counterofferFiles: offerData.counterofferFiles,
      reformulation_text: offerData.reformulation_text,
      reformulationText: offerData.reformulationText,
      reformulation_content: offerData.reformulation_content,
      reformulationFiles: offerData.reformulationFiles,
      reformulation_deadline: offerData.reformulation_deadline,
      reformulationDeadline: offerData.reformulationDeadline
    });
    
    // Generar nombre descriptivo del archivo
    const generateFileName = (mappedData) => {
      const referencia = mappedData.referencia || 'Sin referencia';
      const nombreParte = mappedData.ofertante.nombre && mappedData.ofertante.apellidos 
        ? `${mappedData.ofertante.nombre} ${mappedData.ofertante.apellidos}`.trim()
        : 'Sin nombre';
      const fecha = formatDate(mappedData.creationDate);
      
      // Limpiar caracteres especiales que pueden causar problemas en nombres de archivo
      const cleanString = (str) => {
        return str.replace(/[<>:"/\\|?*]/g, '').replace(/\s+/g, ' ').trim();
      };
      
      const cleanReferencia = cleanString(referencia);
      const cleanNombre = cleanString(nombreParte);
      const cleanFecha = fecha.replace(/\//g, '-');
      
      return `Oferta vinculante (${cleanReferencia}) - ${cleanNombre} - ${cleanFecha}.pdf`;
    };
    
    const fileName = generateFileName(mappedData);
    
    // Crear estilos CSS específicos para el PDF - usando el estilo del PreviewModal
    const styles = `
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
        
        /* Variables CSS */
        :root {
          --color-primary: #333;
          --color-secondary: #666;
          --color-accent: #ff7057;
          --color-accent-light: #ffb3a3;
          --color-blue: rgb(0,0,73);
          --color-light-blue: #A8C7E7;
          --font-primary: 'Inter', 'Avenir Next', 'Segoe UI', 'Roboto', sans-serif;
          --font-secondary: 'Inter', 'Avenir Next', sans-serif;
        }
        
        /* Reset y base */
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          font-family: var(--font-primary);
          background-color: white;
          line-height: 1.5;
          color: var(--color-primary);
        }
        
        /* Contenedor principal */
        .modal-content {
          background-color: white;
          width: 100%;
          max-width: 1100px;
          margin: 0 auto;
        }
        
        /* Header con branding */
        .brand-container {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.75rem 1rem 0.75rem 1rem;
          border-bottom: none;
        }
        
        .brand-left {
          display: flex;
          align-items: center;
        }
        
        .logo {
          height: 20px;
          margin-right: 0.5rem;
          filter: brightness(0.7) contrast(1.4) saturate(1.2);
        }
        
        .brand-name {
          font-size: 1.2rem;
          font-weight: normal;
          font-family: var(--font-primary);
        }
        
        .brand-name span:first-child {
          color: var(--color-primary);
          font-weight: normal;
        }
        
        .brand-name span:last-child {
          color: var(--color-primary);
          font-weight: 800;
        }
        
        .header-info {
          text-align: left;
          font-family: var(--font-secondary);
        }
        
        .ref-line {
          font-size: 0.65rem;
          font-weight: bold;
          color: var(--color-blue);
          margin-bottom: 0.2rem;
        }
        
        .offer-type {
          font-size: 0.65rem;
          margin-bottom: 0.2rem;
        }
        
        .blue-text {
          color: var(--color-blue);
          font-weight: bold;
        }
        
        .orange-text {
          color: var(--color-accent);
          font-weight: bold;
        }
        
        .deadline {
          font-size: 0.65rem;
          color: var(--color-accent);
          margin-bottom: 0;
        }
        
        /* Cuerpo del modal */
        .modal-body {
          padding: 1rem 1rem 1rem 1rem;
        }
        
        /* Bloques de información */
        .info-block {
          margin-bottom: 0.75rem;
          border-bottom: 1px solid var(--color-light-blue);
          padding-bottom: 0.75rem;
        }
        
        .info-block:last-child {
          border-bottom: none;
        }
        
        .info-block.no-bottom-border {
          border-bottom: none;
        }
        
        .block-title {
          font-size: 0.9rem;
          font-weight: 600;
          color: var(--color-light-blue);
          margin-bottom: 0.5rem;
          font-family: var(--font-primary);
          letter-spacing: 0.01em;
        }
        
        /* Grid de tabla */
        .table-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
        }
        
        .info-item {
          font-size: 0.8rem;
          font-family: var(--font-secondary);
          color: var(--color-primary);
          font-weight: 400;
        }
        
        .bold-info-item {
          font-size: 0.8rem;
          font-family: var(--font-secondary);
          color: var(--color-primary);
          font-weight: bold;
        }
        
        .label-item {
          font-size: 0.8rem;
          font-family: var(--font-secondary);
          color: var(--color-primary);
          font-weight: 400;
        }
        
        .full-width {
          grid-column: 1 / 5;
        }
        
        /* Grid especial para cantidades */
        .cantidad-grid {
        }
        
        .cantidad-grid:last-of-type {
        }
        
        .cantidad-label {
          grid-column: 1 / 3;
        }
        
        .cantidad-value {
          grid-column: 3;
        }
        
        /* Texto de contenido */
        .content-text {
          font-size: 0.8rem;
          white-space: pre-line;
          font-family: var(--font-secondary);
          color: var(--color-primary);
          font-weight: 600;
        }
        
        /* Header de oferta */
        .offer-header {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          margin-bottom: 1rem;
          padding-top: 0.75rem;
        }
        
        .offer-title-section {
          display: flex;
          align-items: baseline;
        }
        
        .offer-title {
          font-size: 1.1rem;
          font-weight: 800;
          color: var(--color-accent);
          font-family: var(--font-primary);
          margin: 0;
          margin-right: 0.5rem;
          letter-spacing: 0.01em;
        }
        
        .confidential-text {
          font-size: 1.1rem;
          font-weight: 400;
          color: var(--color-accent);
          font-family: var(--font-secondary);
          margin-left: 0.5rem;
        }
        
        .sent-by-text {
          font-size: 0.8rem;
          font-weight: 400;
          color: var(--color-blue);
          font-family: var(--font-secondary);
        }
        
        /* Componentes de estado */
        .status-container {
          background: white;
          margin-bottom: 0.75rem;
          margin-top: 0.75rem;
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 0;
          width: fit-content;
        }
        
        .timeline-item {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 0.25rem;
          width: fit-content;
          position: relative;
        }
        
        .state-item {
          position: relative;
          width: fit-content;
          flex: 0 0 auto;
          padding-right: 1.5rem;
        }
        
        .state-item:last-child {
          padding-right: 0;
        }
        
        .state-item::after {
          content: "▶";
          color: var(--color-accent-light);
          font-size: 0.8rem;
          position: absolute;
          right: 0.3rem;
          top: 0;
          display: block;
        }
        
        .state-item:last-child::after,
        .state-item.is-last::after {
          display: none;
        }
        
        .line-segment {
          height: 2px;
          background-color: var(--color-accent);
          width: 100%;
          position: relative;
          margin: 0;
          align-self: flex-start;
        }
        
        .line-segment.is-last {
          height: 2px;
          background-color: var(--color-accent);
        }
        
        .line-segment:not(.is-last) {
          height: 1px;
          background-color: var(--color-accent-light);
        }
        
        .date-item {
          width: fit-content;
          flex: 0 0 auto;
          text-align: left;
        }
        
        .status-text {
          font-size: 0.7rem;
          font-weight: 600;
          color: var(--color-accent);
          font-family: var(--font-secondary);
          text-transform: capitalize;
        }
        
        .status-text:not(.is-last) {
          color: var(--color-accent-light);
        }
        
        .status-date {
          font-size: 0.6rem;
          color: var(--color-accent);
          font-family: var(--font-secondary);
          line-height: 1.1;
        }
        
        .status-date:not(.is-last) {
          color: var(--color-accent-light);
        }
        
        /* Contenido rico */
        .rich-content {
          font-size: 0.8rem;
          line-height: 1.3;
          font-family: var(--font-secondary);
          color: var(--color-primary);
          font-weight: 600;
        }
        
        .rich-content p {
          margin-bottom: 0.4rem;
        }
        
        .rich-content p:last-child {
          margin-bottom: 0;
        }
        
        .rich-content ul, .rich-content ol {
          margin-left: 1rem;
          margin-bottom: 0.4rem;
        }
        
        .rich-content strong {
          font-weight: 800;
        }
        
        /* Contenedor de archivos */
        .file-container {
          display: flex;
          align-items: center;
          gap: 0.3rem;
          margin-top: 0.5rem;
          padding-top: 0.3rem;
        }
        
        .file-icon {
          color: #75a9f9;
          margin-right: 0.3rem;
          font-size: 1rem;
        }
        
        .file-name {
          font-size: 0.7rem;
          font-weight: 400;
          font-family: var(--font-secondary);
          color: var(--color-primary);
        }
        
        /* Secciones específicas del PDF */
        .files-section {
          margin: 0.75rem 0;
        }
        
        .files-title {
          font-size: 0.8rem;
          font-weight: 800;
          color: var(--color-accent);
          margin-bottom: 0.35rem;
          padding-top: 0.5rem;
        }
        
        .file-item {
          font-size: 0.7rem;
          color: var(--color-primary);
          margin-bottom: 0.2rem;
        }
        
        .legal-warning {
          border: 1px solid var(--color-light-blue);
          background-color: #f9fbff;
          color: var(--color-primary);
          padding: 0.5rem;
          border-radius: 4px;
          font-size: 0.7rem;
          margin: 1rem 0;
        }
      </style>
    `;
    
    // Determinar el estado de la oferta y crear timeline
    const buildTimelinePdf = (offerData) => {
      const timeline = [];
      
      // Format date function
      const formatStatusDate = (dateString) => {
        if (!dateString) return { date: "", time: "" };
        const date = new Date(dateString);
        const dateStr = date.toLocaleDateString("es-ES", {
          day: "2-digit",
          month: "2-digit",
          year: "2-digit",
        });
        const timeStr = date.toLocaleTimeString("es-ES", {
          hour: "2-digit",
          minute: "2-digit",
        });
        return { date: dateStr, time: timeStr };
      };
      
      const getStatusText = (status) => {
        const statusMap = {
          draft: "Borrador",
          sent: "Enviada",
          accepted: "Aceptada",
          rejected: "Rechazada",
          expired: "Vencida",
          counteroffered: "Contraofertada",
          expiring_soon: "Vence en 5 días",
          reformulated: "Reformulado",
          reformulation_accepted: "Aceptado",
          reformulation_rejected: "Rechazado",
          signing: "Firmando",
          partial_signed: "Firmado parcialmente",
          signed: "Firmado",
        };
        return statusMap[status] || status;
      };
      
      // 1. BORRADOR - Siempre es el primer estado (fecha de creación)
      if (offerData.creationDate) {
        timeline.push({
          status: "draft",
          date: offerData.creationDate,
          text: getStatusText("draft"),
        });
      }

      // 2. ENVIADO - Si la oferta fue enviada
      if (
        offerData.sentDate ||
        [
          "sent",
          "accepted",
          "rejected",
          "counteroffered",
          "reformulated",
          "reformulation_accepted",
          "reformulation_rejected",
          "finalized",
          "expired",
        ].includes(offerData.status)
      ) {
        timeline.push({
          status: "sent",
          date: offerData.sentDate || offerData.lastStatusUpdateDate || offerData.creationDate,
          text: getStatusText("sent"),
        });
      }

      // 3. CONTRAOFERTADO - Si hay contraoferta
      if (
        offerData.counteroffer_date ||
        offerData.counteroffer_user_id ||
        offerData.counteroffer_text ||
        [
          "counteroffered",
          "reformulated",
          "reformulation_accepted",
          "reformulation_rejected"
        ].includes(offerData.status)
      ) {
        timeline.push({
          status: "counteroffered",
          date: offerData.counteroffer_date || offerData.lastStatusUpdateDate,
          text: getStatusText("counteroffered"),
        });
      }

      // 4. REFORMULADO - Si hay reformulación
      if (
        offerData.reformulation_date ||
        offerData.reformulation_content ||
        offerData.reformulationText ||
        [
          "reformulated",
          "reformulation_accepted",
          "reformulation_rejected"
        ].includes(offerData.status)
      ) {
        timeline.push({
          status: "reformulated",
          date: offerData.reformulation_date || offerData.lastStatusUpdateDate,
          text: getStatusText("reformulated"),
        });
      }

      // 5. ESTADOS FINALES - Solo uno de estos puede existir por oferta

      // 5.1 Aceptado (oferta original)
      if (offerData.status === "accepted") {
        timeline.push({
          status: "accepted",
          date: offerData.acceptance_date || offerData.acceptanceDate || offerData.offer_response_date || offerData.lastStatusUpdateDate,
          text: getStatusText("accepted"),
        });
      }

      // 5.2 Rechazado (oferta original)
      else if (offerData.status === "rejected") {
        timeline.push({
          status: "rejected",
          date: offerData.rejection_date || offerData.rejectionDate || offerData.offer_response_date || offerData.lastStatusUpdateDate,
          text: getStatusText("rejected"),
        });
      }

      // 5.3 Reformulación aceptada
      else if (offerData.status === "reformulation_accepted") {
        timeline.push({
          status: "reformulation_accepted",
          date: offerData.reformulation_acceptance_date || offerData.reformulation_response_date || offerData.lastStatusUpdateDate,
          text: getStatusText("reformulation_accepted"),
        });
      }

      // 5.4 Estados de firma electrónica - Pueden seguir a accepted o reformulation_accepted
      else if (offerData.status === "signing") {
        // Primero agregar el estado de aceptación previo
        if (offerData.reformulation_acceptance_date || offerData.reformulation_response_date) {
          // Vino de reformulación
          timeline.push({
            status: "reformulation_accepted",
            date: offerData.reformulation_acceptance_date || offerData.reformulation_response_date || offerData.lastStatusUpdateDate,
            text: getStatusText("reformulation_accepted"),
          });
        } else {
          // Vino de aceptación directa
          timeline.push({
            status: "accepted",
            date: offerData.acceptance_date || offerData.acceptanceDate || offerData.offer_response_date || offerData.lastStatusUpdateDate,
            text: getStatusText("accepted"),
          });
        }

        // Agregar estado de firmando
        timeline.push({
          status: "signing",
          date: offerData.signing_initiated_date || offerData.lastStatusUpdateDate,
          text: getStatusText("signing"),
        });
      }

      // 5.5 Firmado parcialmente
      else if (offerData.status === "partial_signed") {
        // Primero agregar el estado de aceptación previo
        if (offerData.reformulation_acceptance_date || offerData.reformulation_response_date) {
          // Vino de reformulación
          timeline.push({
            status: "reformulation_accepted",
            date: offerData.reformulation_acceptance_date || offerData.reformulation_response_date || offerData.lastStatusUpdateDate,
            text: getStatusText("reformulation_accepted"),
          });
        } else {
          // Vino de aceptación directa
          timeline.push({
            status: "accepted",
            date: offerData.acceptance_date || offerData.acceptanceDate || offerData.offer_response_date || offerData.lastStatusUpdateDate,
            text: getStatusText("accepted"),
          });
        }

        // Agregar estado de firmando si existe fecha
        if (offerData.signing_initiated_date) {
          timeline.push({
            status: "signing",
            date: offerData.signing_initiated_date,
            text: getStatusText("signing"),
          });
        }

        // Agregar estado de firmado parcialmente
        timeline.push({
          status: "partial_signed",
          date: offerData.lastStatusUpdateDate,
          text: getStatusText("partial_signed"),
        });
      }

      // 5.6 Firmado completamente
      else if (offerData.status === "signed") {
        // Primero agregar el estado de aceptación previo
        if (offerData.reformulation_acceptance_date || offerData.reformulation_response_date) {
          // Vino de reformulación
          timeline.push({
            status: "reformulation_accepted",
            date: offerData.reformulation_acceptance_date || offerData.reformulation_response_date || offerData.lastStatusUpdateDate,
            text: getStatusText("reformulation_accepted"),
          });
        } else {
          // Vino de aceptación directa
          timeline.push({
            status: "accepted",
            date: offerData.acceptance_date || offerData.acceptanceDate || offerData.offer_response_date || offerData.lastStatusUpdateDate,
            text: getStatusText("accepted"),
          });
        }

        // Agregar estado de firmando si existe fecha
        if (offerData.signing_initiated_date) {
          timeline.push({
            status: "signing",
            date: offerData.signing_initiated_date,
            text: getStatusText("signing"),
          });
        }

        // Agregar estado de firmado completamente
        timeline.push({
          status: "signed",
          date: offerData.signing_completed_date || offerData.lastStatusUpdateDate,
          text: getStatusText("signed"),
        });
      }

      // 5.7 Reformulación rechazada
      else if (offerData.status === "reformulation_rejected") {
        timeline.push({
          status: "reformulation_rejected",
          date: offerData.reformulation_rejection_date || offerData.reformulation_response_date || offerData.lastStatusUpdateDate,
          text: getStatusText("reformulation_rejected"),
        });
      }

      // 5.8 Finalizado/Firmado (legacy)
      else if (offerData.status === "finalized") {
        timeline.push({
          status: "finalized",
          date: offerData.finalizationDate || offerData.lastStatusUpdateDate,
          text: getStatusText("finalized"),
        });
      }

      // 5.9 Vencido - Este puede ocurrir en cualquier momento después de enviado
      else if (offerData.status === "expired") {
        timeline.push({
          status: "expired",
          date: offerData.expirationDate || offerData.deadlineToRespond || offerData.lastStatusUpdateDate,
          text: getStatusText("expired"),
        });
      }
      
      return timeline.map((item, index) => {
        const { date, time } = formatStatusDate(item.date);
        const isLastItem = index === timeline.length - 1;
        return { ...item, date, time, isLast: isLastItem };
      });
    };
    
    const timeline = buildTimelinePdf(offerData);
    
    // Determinar cuantía superior a 2000€
    const numericAmount = parseFloat(mappedData.monto.replace(/[.€\s]/g, '').replace(',', '.')) || 0;
    const isAmountOver2000 = numericAmount > 2000;
    
    // En PDF siempre es "Oferta Vinculante" - no detectar automáticamente contraoferta
    const isCounterOffer = false;
    
    // Construir el HTML del PDF usando la estructura del PreviewModal
    const pdfContent = `
      <!DOCTYPE html>
      <html lang="es">
      <head>
        <meta charset="UTF-8">
        <title>${isCounterOffer ? 'Contraoferta' : 'Oferta'} Vinculante - ${mappedData.referencia}</title>
        ${styles}
      </head>
      <body>
        <div class="modal-content">
          <!-- Header con branding -->
          <div class="brand-container">
            <div class="brand-left">
              <svg class="logo" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 1128.24">
                <path fill="#95B9E3" d="M981.32,4.35l-356.27,501.59c-24.75,34.84-24.75,81.52,0,116.36l356.27,501.59c5.81,8.17,18.68,4.07,18.68-5.96V10.31c0-10.03-12.88-14.13-18.68-5.96Z"/>
                <path fill="#020048" d="M18.68,4.35l356.27,501.59c24.75,34.84,24.75,81.52,0,116.36L18.68,1123.89c-5.81,8.17-18.68,4.07-18.68-5.96V10.31C0,.29,12.88-3.82,18.68,4.35Z"/>
              </svg>
              <div class="brand-name">
                <span>Ofertas</span>
                <span><strong>Vinculantes</strong></span>
              </div>
            </div>
            <div class="header-info">
              <div class="ref-line">REF.PROPIA ${mappedData.referencia}</div>
              <div class="offer-type">
                <span class="blue-text">${isCounterOffer ? 'Contraoferta' : 'Oferta'} Vinculante</span> <span class="orange-text">${mappedData.confidencialidad ? 'Confidencial' : 'No confidencial'}</span>
              </div>
              <div class="deadline">Plazo respuesta ▶ ${mappedData.plazoRespuesta}</div>
            </div>
          </div>
          
          <div class="modal-body">
            <!-- Representante de la parte ofertante -->
            <div class="info-block">
              <h3 class="block-title">Representante de la parte ofertante</h3>
              <div class="table-grid">
                <div class="bold-info-item">${mappedData.representante.nombre}</div>
                <div class="bold-info-item">${mappedData.representante.apellidos}</div>
                <div class="bold-info-item">${mappedData.representante.email}</div>
                <div class="bold-info-item">${mappedData.representante.movil}</div>
                ${mappedData.representante.numColegiado ? `
                <div class="label-item">Núm de Colegiado</div>
                <div class="info-item"></div>
                <div class="label-item">DNI, NIE</div>
                <div class="info-item"></div>
                <div class="bold-info-item">${mappedData.representante.numColegiado}</div>
                <div class="bold-info-item">${mappedData.representante.provinciaColegio}</div>
                <div class="bold-info-item">${mappedData.representante.identificacion}</div>
                <div class="info-item"></div>
                ` : ''}
              </div>
            </div>
            
            <!-- Datos de la parte ofertante -->
            <div class="info-block">
              <h3 class="block-title">Datos de la parte ofertante</h3>
              <div class="table-grid">
                <div class="bold-info-item">${mappedData.ofertante.nombre}</div>
                <div class="bold-info-item">${mappedData.ofertante.apellidos}</div>
                <div class="bold-info-item">${mappedData.ofertante.email}</div>
                <div class="bold-info-item">${mappedData.ofertante.movil || '-'}</div>
                <div class="label-item full-width">DNI, NIE, NIF/CIF, Pasaporte</div>
                <div class="bold-info-item">${mappedData.ofertante.identificacion}</div>
              </div>
            </div>
            
            <!-- Objeto de la controversia -->
            <div class="info-block">
              <h3 class="block-title">Objeto de la controversia</h3>
              <div class="content-text">${mappedData.objeto}</div>
              <div class="table-grid cantidad-grid">
                <div class="info-item cantidad-label">Cuantía objeto de la controversía superior a 2.000 €</div>
                <div class="info-item cantidad-value">${isAmountOver2000 ? 'Sí' : 'No'}</div>
              </div>
              <div class="table-grid cantidad-grid">
                <div class="info-item cantidad-label">Cuantía objeto de la controversía</div>
                <div class="info-item cantidad-value">${mappedData.monto}</div>
              </div>
            </div>
            
            <!-- Datos de la contraparte -->
            <div class="info-block">
              <h3 class="block-title">Datos de la contraparte</h3>
              <div class="table-grid">
                <div class="bold-info-item">${mappedData.contraparte.nombre}</div>
                <div class="bold-info-item">${mappedData.contraparte.apellidos}</div>
                <div class="bold-info-item">${mappedData.contraparte.email}</div>
                <div class="bold-info-item">${mappedData.contraparte.movil || '-'}</div>
                <div class="label-item full-width">DNI, NIE, NIF/CIF, Pasaporte</div>
                <div class="bold-info-item">${mappedData.contraparte.identificacion}</div>
              </div>
            </div>
            
            <!-- Representante de la contraparte -->
            ${mappedData.representacionContraparte && mappedData.representacionContraparte.activa ? `
            <div class="info-block">
              <h3 class="block-title">Representante de la contraparte</h3>
              <div class="table-grid">
                <div class="bold-info-item">${mappedData.representacionContraparte.nombre || '-'}</div>
                <div class="bold-info-item">${mappedData.representacionContraparte.apellidos || '-'}</div>
                <div class="bold-info-item">${mappedData.representacionContraparte.email || '-'}</div>
                <div class="bold-info-item">${mappedData.representacionContraparte.movil || '-'}</div>
                <div class="label-item full-width">DNI, NIE</div>
                <div class="bold-info-item">${mappedData.representacionContraparte.identificacion || '-'}</div>
              </div>
            </div>
            ` : ''}
            
            <!-- Oferta Vinculante Header -->
            <div class="offer-header">
              <div class="offer-title-section">
                <h3 class="offer-title">${isCounterOffer ? 'Contraoferta' : 'Oferta'} Vinculante</h3>
                <span class="confidential-text">${mappedData.confidencialidad ? 'Confidencial' : 'No confidencial'}</span>
              </div>
              <div class="sent-by-text">
                Enviado por ▶ ${mappedData.ofertante.nombre} ${mappedData.ofertante.apellidos}
              </div>
            </div>

            <!-- Estado -->
            <div class="status-container">
              ${timeline.map((item, index) => `
                <div class="timeline-item">
                  <div class="state-item ${item.isLast ? 'is-last' : ''}">
                    <div class="status-text ${item.isLast ? 'is-last' : ''}">${item.text}</div>
                  </div>
                  <div class="line-segment ${item.isLast ? 'is-last' : ''}"></div>
                  <div class="date-item">
                    <div class="status-date ${item.isLast ? 'is-last' : ''}">
                      ${item.date}<br>
                      ${item.time}
                    </div>
                  </div>
                </div>
              `).join('')}
            </div>
            
            <!-- Contenido de la oferta -->
            <div class="info-block no-bottom-border">
              ${mappedData.archivos && mappedData.archivos.length > 0 ? `
                <div class="file-container">
                  <div class="file-icon">📎</div>
                  <div class="file-name">${mappedData.archivos.map(archivo => archivo.name || 'Archivo sin nombre').join(', ')}</div>
                </div>
              ` : `
                <div class="rich-content">${mappedData.contenido || '-'}</div>
              `}
            </div>

            <!-- Contraoferta -->
            ${mappedData.contraoferta && (mappedData.contraoferta.texto || (mappedData.contraoferta.archivos && mappedData.contraoferta.archivos.length > 0)) ? `
            <div class="files-section">
              <div class="files-title">Contraoferta Vinculante</div>
              <div style="font-size: 0.85rem; color: var(--color-primary); margin-bottom: 0.75rem;">
                Respuesta de la contraparte a la oferta vinculante
              </div>
              
              ${mappedData.contraoferta.archivos && mappedData.contraoferta.archivos.length > 0 ? `
              <div>
                <div class="block-title">Archivos adjuntos de la contraoferta:</div>
                ${mappedData.contraoferta.archivos.map(archivo => `
                  <div class="file-item">📄 ${archivo.name || 'Archivo sin nombre'}</div>
                `).join('')}
              </div>
              ` : ''}
              
              ${mappedData.contraoferta.texto ? `
              <div class="rich-content">${mappedData.contraoferta.texto}</div>
              ` : (!mappedData.contraoferta.archivos || mappedData.contraoferta.archivos.length === 0 ? `
              <div style="font-size: 0.9rem; color: var(--color-primary); font-style: italic;">
                Sin contenido escrito en la contraoferta
              </div>
              ` : '')}
            </div>
            ` : ''}

            <!-- Reformulación -->
            ${mappedData.reformulacion && (mappedData.reformulacion.texto || (mappedData.reformulacion.archivos && mappedData.reformulacion.archivos.length > 0)) ? `
            <div class="files-section">
              <div class="files-title">Reformulación Vinculante</div>
              <div style="font-size: 0.85rem; color: var(--color-primary); margin-bottom: 0.75rem;">
                Reformulación de la oferta en respuesta a la contraoferta recibida
              </div>
              
              ${mappedData.reformulacion.nuevaFechaLimite ? `
              <div>
                <div class="block-title">Nueva fecha límite: ${mappedData.reformulacion.nuevaFechaLimite}</div>
              </div>
              ` : ''}
              
              ${mappedData.reformulacion.archivos && mappedData.reformulacion.archivos.length > 0 ? `
              <div>
                <div class="block-title">Archivos adjuntos de la reformulación:</div>
                ${mappedData.reformulacion.archivos.map(archivo => `
                  <div class="file-item">📄 ${archivo.name || 'Archivo sin nombre'}</div>
                `).join('')}
              </div>
              ` : ''}
              
              ${mappedData.reformulacion.texto ? `
              <div class="rich-content">${mappedData.reformulacion.texto}</div>
              ` : (!mappedData.reformulacion.archivos || mappedData.reformulacion.archivos.length === 0 ? `
              <div style="font-size: 0.9rem; color: var(--color-primary); font-style: italic;">
                Sin contenido escrito en la reformulación
              </div>
              ` : '')}
            </div>
            ` : ''}
            
            <!-- Advertencia legal -->
            <!-- 
            <div class="legal-warning">
              <strong>ADVERTENCIA:</strong> Esta oferta constituye el primer paso de un procedimiento de resolución de conflictos alternativo y extrajudicial basado en los artículos 517 a 542 de la Ley de Enjuiciamiento Civil. La comunicación de la oferta de acuerdo produce efectos interruptivos en la prescripción y caducidad según los artículos 1973 del Código Civil y 30 de la Ley de Enjuiciamiento Civil.
            </div>
            -->
          </div>
        </div>
      </body>
      </html>
    `;
    
    container.innerHTML = pdfContent;
    
    // Ocultar scroll temporalmente durante la generación del PDF
    originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    
    document.body.appendChild(container);
    
    // Configurar las opciones de html2pdf
    const options = {
      margin: [5, 8],
      filename: fileName,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, logging: false },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    
    // Generar el PDF
    await html2pdf.default()
      .from(container)
      .set(options)
      .save();
    
    // Limpiar el DOM y restaurar scroll
    document.body.removeChild(container);
    document.body.style.overflow = originalOverflow;
    
    return true;
  } catch (error) {
    console.error("Error al generar el PDF:", error);
    
    // Limpiar y restaurar en caso de error
    if (container && document.body.contains(container)) {
      document.body.removeChild(container);
    }
    document.body.style.overflow = originalOverflow;
    
    if (error.response) {
      throw new Error(error.response.data.message || 'Error downloading offer PDF');
    }
    throw error;
  }
};

// Download legal certificate for expired or rejected offers
export const downloadLegalCertificate = async (offerId) => {
  try {
    const response = await api.get(`/offers/${offerId}/legal-certificate`, {
      responseType: 'blob' // Important for downloading files
    });
    
    // Crear un enlace temporal para descargar el archivo
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const timestamp = new Date().toISOString().slice(0,16).replace(/[:-]/g, '');
    link.setAttribute('download', `Certificado de Procedibilidad Expediente ${offerId} ${timestamp}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    if (error.response) {
      // Handle specific error cases
      switch (error.response.status) {
        case 400:
          throw new Error("Esta oferta no tiene estado 'expirada' o 'rechazada'. El certificado legal solo está disponible para ofertas con estos estados.");
        case 401:
          throw new Error("No estás autenticado. Por favor, inicia sesión de nuevo.");
        case 403:
          throw new Error("No tienes permisos para acceder a esta oferta.");
        case 404:
          throw new Error("La oferta no fue encontrada.");
        case 500:
          throw new Error("Error interno del servidor. Por favor, inténtalo más tarde.");
        default:
          throw new Error(error.response.data.message || 'Error descargando el certificado legal');
      }
    }
    throw new Error("No se pudo conectar con el servidor. Por favor, verifica tu conexión a internet.");
  }
};

// Support Messages API

/**
 * Send support message to the development team
 * @param {Object} messageData - The message data
 * @param {string} messageData.email - Sender's email
 * @param {string} messageData.subject - Message subject
 * @param {string} messageData.body - Message content
 * @returns {Promise} API response
 */
export const sendSupportMessage = async (messageData) => {
  try {
    const response = await api.post("/msg", {
      email: messageData.email.trim(),
      subject: messageData.subject.trim(),
      body: messageData.body.trim()
    });
    return response.data;
  } catch (error) {
    if (error.response) {
      // Handle specific error cases
      switch (error.response.status) {
        case 400:
          throw new Error("Los datos proporcionados no son válidos. Por favor, verifica que todos los campos estén completos y el email tenga un formato correcto.");
        case 500:
          throw new Error("Error interno del servidor. Por favor, inténtalo de nuevo más tarde.");
        default:
          throw new Error(error.response.data.message || `Error enviando mensaje: ${error.response.status}`);
      }
    }
    throw new Error("No se pudo enviar el mensaje. Por favor, verifica tu conexión a internet e inténtalo de nuevo.");
  }
};

// Download all offers as Excel file
export const downloadOffersExcel = async (params = {}) => {
  try {
    // Build query string from params
    const queryParams = new URLSearchParams();
    
    // Add filters if provided
    if (params.status) {
      queryParams.append('status', params.status);
    }
    if (params.startDate) {
      queryParams.append('start_date', params.startDate);
    }
    if (params.endDate) {
      queryParams.append('end_date', params.endDate);
    }
    if (params.search) {
      queryParams.append('search', params.search);
    }
    
    const response = await api.get(`/offers/excel?${queryParams.toString()}`, {
      responseType: 'blob',
    });
    
    // Create blob link to download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    // Get filename from Content-Disposition header or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'ofertas.xlsx';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return { success: true, filename };
  } catch (error) {
    console.error('Error downloading Excel:', error);
    throw error;
  }
};

// Purchase API services

// Get available packages
export const getPackages = async () => {
  try {
    const response = await api.get("/packages");
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data.message || `Request failed with status code ${error.response.status}`);
    }
    throw error;
  }
};

// Get user credits information
export const getUserCredits = async () => {
  try {
    const response = await api.get("/user/credits");
    return response.data;
  } catch (error) {
    if (error.response) {
      // Handle specific error codes
      switch (error.response.status) {
        case 401:
          throw new Error("Usuario no autenticado");
        case 500:
          throw new Error("Error interno del servidor al obtener información de créditos");
        default:
          throw new Error(error.response.data.message || `Request failed with status code ${error.response.status}`);
      }
    }
    throw new Error("Unable to fetch credit information");
  }
};

// Generate Excel file in frontend using xlsx library
export const generateOffersExcel = async (offers, filters = {}) => {
  try {
    // Import xlsx dynamically to avoid SSR issues

    const XLSX = await import('xlsx');
    
    console.log("📊 Generating Excel with offers:", offers.length);
    
    // Función para traducir estados
    const translateStatus = (status) => {
      switch (status) {
        case "draft": return "Borrador";
        case "sent": return "Enviada";
        case "accepted": return "Aceptada";
        case "rejected": return "Rechazada";
        case "counteroffering": return "Contraoferta";
        case "counteroffered": return "Contraofertada";
        case "expired": return "Vencido";
        case "expiring_in_5_days": return "Vence en 5 días";
        case "finalized": return "Finalizada";
        case "reformulating": return "Reformulando";
        case "reformulated": return "Reformulado";
        case "accepting_reformulation": return "Aceptando reformulación";
        case "rejecting_reformulation": return "Rechazando reformulación";
        case "reformulation_accepted": return "Reformulado aceptado";
        case "reformulation_rejected": return "Reformulado rechazado";
        case "signing": return "Firmando";
        case "partial_signed": return "Firmado parcialmente";
        case "signed": return "Firmado";
        default: return status;
      }
    };

    // Función para formatear fechas
    const formatDate = (dateString) => {
      if (!dateString || dateString === '-') return '-';
      try {
        return formatDateToCustomFormat(dateString);
      } catch (error) {
        return dateString;
      }
    };

    // Función para obtener nombre del abogado
    const getLawyerName = (lawyer) => {
      if (!lawyer) return '-';
      if (typeof lawyer === 'string') return lawyer;
      if (typeof lawyer === 'object') {
        const firstName = lawyer.firstName || lawyer.name || '';
        const lastName = lawyer.lastNames || lawyer.lastName || '';
        return `${firstName} ${lastName}`.trim() || '-';
      }
      return '-';
    };

    // Preparar datos para Excel
    const excelData = offers.map(offer => {
      return {
        'Estado': translateStatus(offer.status || '-'),
        'Referencia propia': offer.referencia_expediente || offer.reference || offer.user_reference || '-',
        'Fecha creación': formatDate(offer.fecha_creacion || offer.creationDate),
        'Fecha envío': formatDate(offer.fecha_envio || offer.sentDate),
        'Fecha vencimiento': formatDate(offer.fecha_vencimiento || offer.expirationDate || offer.deadlineToRespond),
        'Cliente': offer.client.firstName + ' ' + offer.client.lastNames || '-',
        'Contraparte': offer.counterparty.firstName + ' ' + offer.counterparty.lastNames || '-',
        'Abogado': getLawyerName(offer.lawyer),
        'Objeto': offer.subject || offer.offer_subject || '-',
        'Cuantía': offer.amount || offer.offer_total_amount || '-',
        'Confidencial': offer.confidential ? 'Sí' : 'No',
        'Error de envío': offer.envio_fallido ? 'Sí' : 'No'
      };
    });

    // Crear workbook y worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Ajustar ancho de columnas
    const columnWidths = [
      { wch: 15 }, // Estado
      { wch: 20 }, // Referencia propia
      { wch: 15 }, // Fecha creación
      { wch: 15 }, // Fecha envío
      { wch: 15 }, // Fecha vencimiento
      { wch: 25 }, // Cliente
      { wch: 25 }, // Contraparte
      { wch: 25 }, // Abogado
      { wch: 40 }, // Objeto
      { wch: 15 }, // Cuantía
      { wch: 12 }, // Confidencial
      { wch: 15 }  // Error de envío
    ];
    worksheet['!cols'] = columnWidths;

    // Agregar worksheet al workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Ofertas Vinculantes');

    // Generar nombre de archivo con fecha
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '-');
    const filename = `ofertas-vinculantes-${dateStr}-${timeStr}.xlsx`;

    // Descargar archivo
    XLSX.writeFile(workbook, filename);
    
    console.log("✅ Excel file generated successfully:", filename);
    return true;
  } catch (error) {
    console.error("Error generating Excel file:", error);
    throw new Error("Error al generar el archivo Excel: " + error.message);
  }
};

// Credit purchase functions with Stripe
export const createPaymentIntent = async (packageType, billingDetails = null, promotionCode = null) => {
  try {
    const requestData = {
      package_type: packageType,
      ...(billingDetails && { billing_details: billingDetails }),
      ...(promotionCode && { promotion_code: promotionCode })
    };
    
    const response = await api.post("/create-payment-intent", requestData);
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data.message || `Error en el pago: ${error.response.status}`);
    }
    throw error;
  }
};

export const validatePromoCode = async (code) => {
  try {
    const response = await api.post("/validate-promo-code", { code });
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Código inválido');
    }
    throw error;
  }
};

export const confirmPayment = async (paymentIntentId) => {
  try {
    const response = await api.post("/confirm", { payment_intent_id: paymentIntentId });
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data.message || `Error confirmando el pago: ${error.response.status}`);
    }
    throw error;
  }
};

// Download invoice from Stripe
export const downloadInvoice = async (paymentIntentId) => {
  try {
    const response = await api.get(`/invoice/${paymentIntentId}`);
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data.message || `Error descargando factura: ${error.response.status}`);
    }
    throw error;
  }
};

// Certificate API services
export const requestClientCertificate = async () => {
  try {
    const response = await api.get("/certificates/request-client-cert");
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data.message || `Request failed with status code ${error.response.status}`);
    }
    throw error;
  }
};

export const validateCertificateForRegistration = async (certificateData, idNumber, idType) => {
  try {
    const response = await api.post("/certificates/validate-registration", {
      certificateData,
      idNumber,
      idType
    });
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data.message || `Request failed with status code ${error.response.status}`);
    }
    throw error;
  }
};

export const validateCertificateForLogin = async (certificateData, idNumber) => {
  try {
    const response = await api.post("/certificates/validate-login", {
      certificateData,
      idNumber
    });
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data.message || `Request failed with status code ${error.response.status}`);
    }
    throw error;
  }
};

export const revalidateCertificate = async (certificateData) => {
  try {
    const response = await api.post("/certificates/revalidate", {
      certificateData
    });
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data.message || `Request failed with status code ${error.response.status}`);
    }
    throw error;
  }
};

// New certificate flow functions
export const generateCertUrl = async () => {
  try {
    const response = await api.get("/certificates/generate-cert-url");
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data.message || `Request failed with status code ${error.response.status}`);
    }
    throw error;
  }
};

export const registerWithCert = async (userData, certificateData) => {
  try {
    const response = await api.post("/certificates/register-with-cert", {
      ...userData,
      certificate: certificateData
    });
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data.message || `Request failed with status code ${error.response.status}`);
    }
    throw error;
  }
};

export const loginWithCert = async (credentials, certificateData) => {
  try {
    const response = await api.post("/certificates/login-with-cert", {
      ...credentials,
      certificate: certificateData
    });
    
    // Store the JWT token in localStorage
    if (response.data.token) {
      localStorage.setItem("token", response.data.token);
      // Set the token in axios default headers
      api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
    }
    
    return response.data;
  } catch (error) {
    // Para el login, preservamos el error original
    throw error;
  }
};

// Electronic Signature API functions
export const initiateSigningProcess = async (offerId, signingData) => {
  try {
    const response = await api.post(`/offers/${offerId}/initiate-signing`, signingData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getSigningStatus = async (offerId) => {
  try {
    const response = await api.get(`/offers/${offerId}/signing-status`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const downloadSignedAgreement = async (offerId) => {
  try {
    const response = await api.get(`/offers/${offerId}/download-signed-agreement`, {
      responseType: 'blob',
    });
    
    // Create a download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `acuerdo_firmado_oferta_${offerId}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return { success: true };
  } catch (error) {
    throw error;
  }
};

export default api;
